import { createRef, type ReactNode } from "react";
import { action, computed, makeObservable, observable } from "mobx";
import { injectable } from "tsyringe";
import { ViewModel } from "@orderofchaos/mvvm";
import {
  buildVisibleChunkIds,
  canTriggerLoadMore,
  resolveMeasuredChunkHeight,
  resolveViewportAction,
  resolveVisibleRange,
  shiftVisibleRangeDown,
  shiftVisibleRangeUp,
  shouldLoadMoreForShortDocument,
  splitIntoChunks,
  sumChunkHeights,
  type VisibleRange,
} from "./viewmodel/virtualization-logic";

export interface VirtualizedChunksProps<T = unknown> {
  items: T[];
  renderChunk: (chunkId: number, chunkItems: T[]) => ReactNode;
  onLoadMore?: () => void | Promise<void>;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  loadingLabel?: string;
  chunkSize?: number;
  windowChunks?: number;
  paddingPx?: number;
  estimatedChunkHeight?: number;
  className?: string;
  contentClassName?: string;
}

@injectable()
export class VirtualizedChunksViewModel extends ViewModel<
  unknown,
  VirtualizedChunksProps<unknown>
> {
  readonly rootRef = createRef<HTMLDivElement>();

  @observable
  private visibleStart = 0;

  @observable
  private visibleEnd = -1;

  @observable
  private heightsVersion = 0;

  private readonly chunkHeights = new Map<number, number>();

  private readonly resizeObservers = new Map<number, ResizeObserver>();

  private viewportRaf: number | null = null;

  private lastLoadRequestAt = 0;

  private readonly minLoadIntervalMs = 700;

  constructor() {
    super();
    makeObservable(this);
  }

  @computed
  get chunks(): unknown[][] {
    return splitIntoChunks(this.viewProps.items || [], this.chunkSize);
  }

  @computed
  get chunkSize(): number {
    return Math.max(1, this.viewProps.chunkSize ?? 8);
  }

  @computed
  get windowChunks(): number {
    return Math.max(1, this.viewProps.windowChunks ?? 2);
  }

  @computed
  get paddingPx(): number {
    return Math.max(0, this.viewProps.paddingPx ?? 120);
  }

  @computed
  get hasMore(): boolean {
    return this.viewProps.hasMore ?? false;
  }

  @computed
  get isLoadingMore(): boolean {
    return this.viewProps.isLoadingMore ?? false;
  }

  @computed
  get loadingLabel(): string {
    return this.viewProps.loadingLabel || "Загрузка...";
  }

  @computed
  get chunksCount(): number {
    return this.chunks.length;
  }

  @computed
  private get measuredChunkHeight(): number {
    this.touchHeightsVersion();
    return resolveMeasuredChunkHeight({
      chunkHeights: this.chunkHeights,
      estimatedChunkHeight: this.viewProps.estimatedChunkHeight ?? 560,
    });
  }

  @computed
  get topSpacerHeight(): number {
    this.touchHeightsVersion();

    return sumChunkHeights(
      0,
      this.visibleStart,
      this.chunkHeights,
      this.measuredChunkHeight,
    );
  }

  @computed
  get bottomSpacerHeight(): number {
    this.touchHeightsVersion();

    return sumChunkHeights(
      this.visibleEnd + 1,
      this.chunksCount,
      this.chunkHeights,
      this.measuredChunkHeight,
    );
  }

  @computed
  get visibleChunksHeight(): number {
    this.touchHeightsVersion();

    return sumChunkHeights(
      this.visibleStart,
      this.visibleEnd + 1,
      this.chunkHeights,
      this.measuredChunkHeight,
    );
  }

  @computed
  get visibleChunkIds(): number[] {
    return buildVisibleChunkIds({
      chunksCount: this.chunksCount,
      visibleStart: this.visibleStart,
      visibleEnd: this.visibleEnd,
    });
  }

  setChunkRef = (chunkId: number, node: HTMLDivElement | null): void => {
    const previousObserver = this.resizeObservers.get(chunkId);
    if (previousObserver) {
      previousObserver.disconnect();
      this.resizeObservers.delete(chunkId);
    }

    if (!node) {
      return;
    }

    const measure = (): void => {
      this.setChunkHeight(chunkId, node.getBoundingClientRect().height);
    };

    measure();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(() => {
      measure();
    });

    observer.observe(node);
    this.resizeObservers.set(chunkId, observer);
  };

  protected onViewMounted(): void {
    this.syncVisibleRange();

    if (typeof window !== "undefined") {
      window.addEventListener("scroll", this.scheduleViewportCheck, {
        passive: true,
      });
      window.addEventListener("resize", this.scheduleViewportCheck);
      this.addDisposer(() => {
        window.removeEventListener("scroll", this.scheduleViewportCheck);
        window.removeEventListener("resize", this.scheduleViewportCheck);
      });
    }

    this.scheduleViewportCheck();
  }

  protected onViewUpdated(): void {
    this.syncVisibleRange();
    this.scheduleViewportCheck();
  }

  protected onViewUnmounted(): void {
    this.resizeObservers.forEach((observer) => observer.disconnect());
    this.resizeObservers.clear();

    if (this.viewportRaf !== null && typeof window !== "undefined") {
      window.cancelAnimationFrame(this.viewportRaf);
      this.viewportRaf = null;
    }
  }

  @action.bound
  private setChunkHeight(chunkId: number, height: number): void {
    const normalizedHeight = Math.max(1, Math.round(height));
    const previous = this.chunkHeights.get(chunkId);

    if (previous === normalizedHeight) {
      return;
    }

    this.chunkHeights.set(chunkId, normalizedHeight);
    this.heightsVersion += 1;
    this.scheduleViewportCheck();
  }

  @action.bound
  private syncVisibleRange(): void {
    this.trimChunkHeights(this.chunksCount);
    const nextRange = resolveVisibleRange({
      chunksCount: this.chunksCount,
      windowChunks: this.windowChunks,
      visibleStart: this.visibleStart,
      visibleEnd: this.visibleEnd,
    });
    this.setVisibleRange(nextRange.start, nextRange.end);
  }

  @action.bound
  private trimChunkHeights(maxChunkIdExclusive: number): void {
    let changed = false;

    this.chunkHeights.forEach((_height, chunkId) => {
      if (chunkId < maxChunkIdExclusive) {
        return;
      }

      this.chunkHeights.delete(chunkId);
      changed = true;
    });

    this.resizeObservers.forEach((observer, chunkId) => {
      if (chunkId < maxChunkIdExclusive) {
        return;
      }

      observer.disconnect();
      this.resizeObservers.delete(chunkId);
    });

    if (changed) {
      this.heightsVersion += 1;
    }
  }

  @action.bound
  private shiftDown(): void {
    const currentRange = this.currentRange;
    const nextRange = shiftVisibleRangeDown(currentRange, this.chunksCount);
    this.setVisibleRange(nextRange.start, nextRange.end);
  }

  @action.bound
  private shiftUp(): void {
    const nextRange = shiftVisibleRangeUp(this.currentRange);
    this.setVisibleRange(nextRange.start, nextRange.end);
  }

  @action.bound
  private setVisibleRange(start: number, end: number): void {
    if (this.visibleStart === start && this.visibleEnd === end) {
      return;
    }

    this.visibleStart = start;
    this.visibleEnd = end;
  }

  private scheduleViewportCheck = (): void => {
    if (typeof window === "undefined") {
      return;
    }

    if (this.viewportRaf !== null) {
      return;
    }

    this.viewportRaf = window.requestAnimationFrame(() => {
      this.viewportRaf = null;
      this.reconcileByViewport();
      this.loadMoreIfListIsShorterThanViewport();
    });
  };

  private reconcileByViewport(): void {
    const root = this.rootRef.current;

    if (!root || this.chunksCount <= 0) {
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    const viewportTop =
      window.scrollY ||
      window.pageYOffset ||
      document.documentElement.scrollTop;
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight || 0;
    const viewportBottom = viewportTop + viewportHeight;

    const rootRect = root.getBoundingClientRect();
    const rootTop = rootRect.top + viewportTop;
    const totalHeight =
      this.topSpacerHeight + this.visibleChunksHeight + this.bottomSpacerHeight;
    const action = resolveViewportAction({
      viewportTop,
      viewportBottom,
      paddingPx: this.paddingPx,
      rootTop,
      totalHeight,
      topSpacerHeight: this.topSpacerHeight,
      visibleChunksHeight: this.visibleChunksHeight,
      visibleEnd: this.visibleEnd,
      chunksCount: this.chunksCount,
    });

    if (action === "shiftDown") {
      this.shiftDown();
      return;
    }

    if (action === "shiftUp") {
      this.shiftUp();
      return;
    }

    if (action === "loadMore") {
      this.loadMore();
    }
  }

  private loadMoreIfListIsShorterThanViewport(): void {
    const root = this.rootRef.current;

    if (!root) {
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight || 0;
    const documentHeight = Math.max(
      document.body?.scrollHeight ?? 0,
      document.documentElement?.scrollHeight ?? 0,
    );
    const rootRect = root.getBoundingClientRect();
    if (
      shouldLoadMoreForShortDocument({
        viewportHeight,
        documentHeight,
        rootTop: rootRect.top,
        rootBottom: rootRect.bottom,
        paddingPx: this.paddingPx,
      })
    ) {
      this.loadMore();
    }
  }

  private loadMore(): void {
    const onLoadMore = this.viewProps.onLoadMore;

    if (
      !canTriggerLoadMore({
        hasOnLoadMore: Boolean(onLoadMore),
        hasMore: this.hasMore,
        isLoadingMore: this.isLoadingMore,
        lastLoadRequestAt: this.lastLoadRequestAt,
        now: Date.now(),
        minLoadIntervalMs: this.minLoadIntervalMs,
      })
    ) {
      return;
    }

    this.lastLoadRequestAt = Date.now();
    if (!onLoadMore) {
      return;
    }

    void onLoadMore();
  }

  private get currentRange(): VisibleRange {
    return {
      start: this.visibleStart,
      end: this.visibleEnd,
    };
  }

  private touchHeightsVersion(): void {
    // Bind computed getters to measured chunk heights updates.
    void this.heightsVersion;
  }
}
